import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { Payment, PaymentSortField, PaymentSortState } from '@models/payments';
import {
  formatDeclineLine,
  formatPaymentAmount,
  formatPaymentDate,
  getDeclineInfo,
  getPaymentCustomer,
  getPaymentTypeLabel,
  getUiPaymentStatus,
} from '@features/payments/helpers';

import LoadingAnimation from '@components/LoadingAnimation';
import PaymentStatusBadge from '../PaymentStatusBadge';
import PaymentMethodChip from '../PaymentMethodChip';
import s from './PaymentsCardList.module.css';

type SortChipProps = {
  label: string;
  field: PaymentSortField;
  sort: PaymentSortState;
  onSort: (field: PaymentSortField) => void;
};

function SortChip({ label, field, sort, onSort }: SortChipProps) {
  const active = sort.field === field;
  return (
    <button
      type="button"
      className={`${s.sortChip} ${active ? s.sortChipActive : ''}`}
      onClick={() => onSort(field)}
      aria-pressed={active}>
      {label}
      {active &&
        (sort.dir === 'asc' ? (
          <ArrowDropUpIcon sx={{ fontSize: 16 }} />
        ) : (
          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
        ))}
    </button>
  );
}

type PaymentsCardListProps = {
  isLoading: boolean;
  isFetching: boolean;
  rows: Payment[];
  sort: PaymentSortState;
  onSort: (field: PaymentSortField) => void;
  onRowClick?: (row: Payment) => void;
};

function PaymentsCardList({
  isLoading,
  isFetching,
  rows,
  sort,
  onSort,
  onRowClick,
}: PaymentsCardListProps) {
  const showOverlay = isFetching && !isLoading && rows.length > 0;

  return (
    <div className={s.shell}>
      <div className={s.sortBar}>
        <span className={s.sortLabel}>Sort by</span>
        <SortChip label="Date" field="date" sort={sort} onSort={onSort} />
        <SortChip label="Amount" field="amount" sort={sort} onSort={onSort} />
      </div>

      <div className={s.scroll}>
        {isLoading ? (
          <div className={s.loading}>
            <LoadingAnimation compact />
          </div>
        ) : rows.length === 0 ? (
          <div className={s.empty}>No payments match your search.</div>
        ) : (
          rows.map((row) => {
            const customer = getPaymentCustomer(row);
            const typeLabel = getPaymentTypeLabel(row);
            const uiStatus = getUiPaymentStatus(row);
            const amountLabel = formatPaymentAmount(
              row.amount,
              row.currency,
              row.currency.decimalDigits,
            );
            const fd = formatPaymentDate(new Date(row.completedAt));
            const declineInfo = getDeclineInfo(row);
            const declineLine = declineInfo ? formatDeclineLine(declineInfo, true) : null;

            return (
              <button
                key={row.id}
                type="button"
                data-status={uiStatus}
                className={s.card}
                onClick={() => onRowClick?.(row)}
                aria-label={`View details for payment ${row.id}`}>
                <div className={s.cardTopRow}>
                  <span className={s.cardIdChip} title={row.id}>
                    <span className={s.cardIdChipText}>
                      {row.id.length > 25 ? `${row.id.slice(0, 25)}…` : row.id}
                    </span>
                    <ChevronRightIcon className={s.cardIdChevron} sx={{ fontSize: 16 }} />
                  </span>
                  <div className={s.statusBlock}>
                    <PaymentStatusBadge status={uiStatus} />
                    {declineLine && (
                      <span className={s.declineLine} title={declineLine}>
                        {declineLine}
                      </span>
                    )}
                  </div>
                </div>

                <div className={s.cardMidRow}>
                  <div className={s.customerBlock}>
                    <div className={s.customerName}>{customer.name || 'N/A'}</div>
                    {customer.email && <div className={s.customerEmail}>{customer.email}</div>}
                  </div>
                  <div className={s.amountBlock}>
                    <span className={s.amountValue}>{amountLabel}</span>
                    <span className={s.amountCurrency}>{row.currency.code}</span>
                  </div>
                </div>

                <div className={s.cardBottomRow}>
                  <div className={s.metaGroup}>
                    <span>
                      {fd.date} · {fd.time}
                    </span>
                    <span className={s.metaSep}>·</span>
                    <span className={s.typeText}>{typeLabel}</span>
                  </div>
                  <PaymentMethodChip
                    customerPaymentMethod={row.customerPaymentMethod}
                    processor={row.processor}
                  />
                </div>
              </button>
            );
          })
        )}
      </div>

      {showOverlay && (
        <div className={s.fetchingOverlay} aria-live="polite" aria-busy="true">
          <LoadingAnimation compact />
        </div>
      )}
    </div>
  );
}

export default PaymentsCardList;
